/**
 * Filters for holidays.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { Select } from '@/components/forms/Select';
import { getVacationZones } from '@/services/holidays.service';
import {
  DATE_RANGE_PERIOD_OPTIONS,
  HOLIDAY_FORM_QUERY_KEYS,
  HOLIDAY_TYPE_CONFIG,
} from './holiday-configs';
import type { HolidayFilters } from './holiday-configs';
import { mapVacationZoneOptions } from './holiday-form-utils';
import {
  parseHolidayFilterEndDate,
  parseHolidayFilterStartDate,
  toHolidayFilterDateString,
} from './holiday-table-utils';

/** Props for {@link HolidayFiltersContent}. */
interface HolidayFiltersContentProps {
  filters: HolidayFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleType: (type: string) => void;
  onSetDateRange: (range: HolidayFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetVacationZone: (vacationZoneId: string) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Toggle chips for holiday type. */
const TypeFilter: React.FC<{
  filters: HolidayFilters;
  onToggleType: (type: string) => void;
}> = ({ filters, onToggleType }) => (
  <div className="filter-group">
    <span className="filter-group-label">Type</span>
    <div className="filter-options">
      {Object.entries(HOLIDAY_TYPE_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const isActive = filters.types.includes(key);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
            onClick={() => onToggleType(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Exclusive period chip; choosing a period clears the custom date range. */
const PeriodFilter: React.FC<{
  filters: HolidayFilters;
  onSetDateRange: (range: HolidayFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetDateRange, onSetCustomDateRange }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Période</span>
      <SegmentedControl
        size="sm"
        value={filters.customDateFrom ? '' : (filters.dateRange ?? 'all')}
        onChange={(v) => {
          onSetDateRange(v as HolidayFilters['dateRange']);
          onSetCustomDateRange(undefined, undefined);
        }}
        options={[...DATE_RANGE_PERIOD_OPTIONS]}
      />
    </div>
  );
};

/** Custom date range; end-of-day on `to` is inclusive. */
const DateRangeFilter: React.FC<{
  filters: HolidayFilters;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetCustomDateRange }) => {
  const fromStr = toHolidayFilterDateString(filters.customDateFrom);
  const toStr = toHolidayFilterDateString(filters.customDateTo);

  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.customDateTo);
      return;
    }
    onSetCustomDateRange(parseHolidayFilterStartDate(val), filters.customDateTo);
  };

  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.customDateFrom, undefined);
      return;
    }
    onSetCustomDateRange(filters.customDateFrom, parseHolidayFilterEndDate(val));
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date personnalisée</span>
      <div className="w-auto">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Vacation-zone select; empty value means every zone plus nationals. */
const ZoneFilter: React.FC<{
  filters: HolidayFilters;
  onSetVacationZone: (vacationZoneId: string) => void;
}> = ({ filters, onSetVacationZone }) => {
  const { data: vacationZones = [] } = useQuery({
    queryKey: HOLIDAY_FORM_QUERY_KEYS.vacationZones,
    queryFn: getVacationZones,
  });
  const zoneOptions = React.useMemo(() => mapVacationZoneOptions(vacationZones), [vacationZones]);

  function handleZoneChange(value: string): void {
    onSetVacationZone(value);
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Zone</span>
      <div className="w-auto">
        <Select
          size="sm"
          value={filters.vacationZoneId ?? ''}
          onChange={handleZoneChange}
          options={zoneOptions}
        />
      </div>
    </div>
  );
};

/** Ribbon of holiday filters; each group is gated by RBAC + configure-panel visibility. */
export const HolidayFiltersContent: React.FC<HolidayFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleType,
  onSetDateRange,
  onSetCustomDateRange,
  onSetVacationZone,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-type') && <TypeFilter filters={filters} onToggleType={onToggleType} />}

      {show('filter-period') && (
        <PeriodFilter
          filters={filters}
          onSetDateRange={onSetDateRange}
          onSetCustomDateRange={onSetCustomDateRange}
        />
      )}

      {show('filter-date-range') && (
        <DateRangeFilter filters={filters} onSetCustomDateRange={onSetCustomDateRange} />
      )}

      {show('filter-zone') && (
        <ZoneFilter filters={filters} onSetVacationZone={onSetVacationZone} />
      )}
    </div>
  );
};
